import React, { useId } from 'react';
import classNames from 'classnames';
import styles from './LiquidGlassContainer.module.css';

export type LiquidGlassBlur = 'soft' | 'medium' | 'intense';
export type LiquidGlassOpacity = 'light' | 'medium' | 'heavy';
export type LiquidGlassRadius = 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type LiquidGlassPadding = 'none' | 'sm' | 'md' | 'lg';
export type LiquidGlassReflex = 'none' | 'minimal' | 'full';
export type LiquidGlassVariant = 'liquid' | 'sheet';
export type LiquidGlassDensity = 'clear' | 'matte';

export interface LiquidGlassContainerProps extends React.HTMLAttributes<HTMLElement> {
	/** Glass variant - 'liquid' (default, shadow down) or 'sheet' (shadow up) */
	variant?: LiquidGlassVariant;
	/** Glass density - 'clear' (transparent, classic glass) or 'matte' (more opaque, frosted) */
	density?: LiquidGlassDensity;
	/** Blur intensity */
	blur?: LiquidGlassBlur;
	/** Background opacity */
	opacity?: LiquidGlassOpacity;
	/** Border radius */
	radius?: LiquidGlassRadius;
	/** Padding */
	padding?: LiquidGlassPadding;
	/** Glass reflex/shine effect */
	reflex?: LiquidGlassReflex;
	/** Enable liquid distortion effect (SVG filter) */
	liquidEffect?: boolean;
	/** Interactive hover/active states */
	interactive?: boolean;
	/** Focused state */
	focused?: boolean;
	/** Error state */
	error?: boolean;
	/** Full width */
	fullWidth?: boolean;
	/** Element to render as */
	as?: React.ElementType;
	/** Additional class name */
	className?: string;
	/** Children */
	children?: React.ReactNode;
}

/**
 * Universal Liquid Glass container component.
 * Provides consistent glass morphism effect across the app.
 *
 * @example
 * // Basic usage (default: liquid variant with shadow down)
 * <LiquidGlassContainer>Content</LiquidGlassContainer>
 *
 * @example
 * // Sheet variant (shadow up - for toasts, bottom sheets)
 * <LiquidGlassContainer
 *   variant="sheet"
 *   blur="intense"
 *   opacity="medium"
 *   radius="xl"
 * >
 *   Toast notification
 * </LiquidGlassContainer>
 *
 * @example
 * // Custom configuration
 * <LiquidGlassContainer
 *   blur="intense"
 *   opacity="heavy"
 *   radius="xl"
 *   padding="lg"
 *   reflex="full"
 * >
 *   Modal content
 * </LiquidGlassContainer>
 *
 * @example
 * // Interactive card
 * <LiquidGlassContainer
 *   as="button"
 *   interactive
 *   blur="soft"
 *   radius="lg"
 *   onClick={handleClick}
 * >
 *   Clickable card
 * </LiquidGlassContainer>
 */
const blurStyles: Record<LiquidGlassBlur, string> = {
	soft: styles['blur--soft'],
	medium: styles['blur--medium'],
	intense: styles['blur--intense'],
};

const opacityStyles: Record<LiquidGlassOpacity, string> = {
	light: styles['opacity--light'],
	medium: styles['opacity--medium'],
	heavy: styles['opacity--heavy'],
};

const radiusStyles: Record<LiquidGlassRadius, string> = {
	sm: styles['radius--sm'],
	md: styles['radius--md'],
	lg: styles['radius--lg'],
	xl: styles['radius--xl'],
	full: styles['radius--full'],
};

const paddingStyles: Record<LiquidGlassPadding, string> = {
	none: styles['padding--none'],
	sm: styles['padding--sm'],
	md: styles['padding--md'],
	lg: styles['padding--lg'],
};


export const LiquidGlassContainer = React.forwardRef<HTMLElement, LiquidGlassContainerProps>(
	(
		{
			variant = 'liquid',
			density = 'matte',
			blur = 'soft',
			opacity = 'medium',
			radius = 'lg',
			padding = 'none',
			reflex = 'full',
			liquidEffect = false,
			interactive = false,
			focused = false,
			error = false,
			fullWidth = false,
			as: Component = 'div',
			className,
			children,
			style,
			...props
		},
		ref
	) => {
		const filterId = useId();
		const filterName = `liquid-glass-filter-${filterId.replace(/:/g, '')}`;

		const containerClassName = classNames(
			styles.container,
			{
				[styles['variant--sheet']]: variant === 'sheet',
				[styles['density--clear']]: density === 'clear',
				[styles['density--matte']]: density === 'matte',
			},
			blurStyles[blur],
			opacityStyles[opacity],
			radiusStyles[radius],
			paddingStyles[padding],
			{
				[styles.reflex]: reflex === 'full' && variant === 'liquid',
				[styles['reflex--minimal']]: reflex === 'minimal' && variant === 'liquid',
				[styles['reflex--sheet']]: variant === 'sheet' && reflex === 'full',
				[styles['reflex--sheet-minimal']]: variant === 'sheet' && reflex === 'minimal',
				[styles.interactive]: interactive,
				[styles.focused]: focused,
				[styles.error]: error,
				[styles.fullWidth]: fullWidth,
			},
			className
		);

		const containerStyle = liquidEffect
			? { ...style, filter: `url(#${filterName})` }
			: style;

		return (
			<Component ref={ref} className={containerClassName} style={containerStyle} {...props}>
				{liquidEffect && (
					<div className={styles.liquidFilter} aria-hidden="true">
						<svg width="0" height="0">
							<filter id={filterName} primitiveUnits="objectBoundingBox">
								<feTurbulence
									type="fractalNoise"
									baseFrequency="0.65 2"
									numOctaves="2"
									seed="2"
									result="map"
								/>
								<feGaussianBlur
									in="SourceGraphic"
									stdDeviation="0.04"
									result="blur"
								/>
								<feDisplacementMap
									in="blur"
									in2="map"
									scale="0.5"
									xChannelSelector="R"
									yChannelSelector="G"
								/>
							</filter>
						</svg>
					</div>
				)}
				{children}
			</Component>
		);
	}
);

LiquidGlassContainer.displayName = 'LiquidGlassContainer';

